import { useEffect, useCallback } from 'react';
import { createLogger } from '@/lib/structuredLogger';

const logger = createLogger('Performance');

interface PerformanceMetrics {
  loadTime: number;
  firstContentfulPaint: number;
  largestContentfulPaint: number;
  firstInputDelay: number;
  cumulativeLayoutShift: number;
  memoryUsage?: number;
}

interface UsePerformanceReturn {
  metrics: PerformanceMetrics | null;
  measureComponent: (name: string) => () => void;
  logPerformance: (metric: string, value: number) => void;
}

export const usePerformance = (): UsePerformanceReturn => {
  const measureComponent = useCallback((_name: string) => {
    // const start = performance.now();
    return () => {
      // const end = performance.now();
      // const duration = end - start;
      if (!import.meta.env.PROD) {
        // console.log(`Component ${_name} render time: ${duration.toFixed(2)}ms`);
      }

      // TODO: Send to analytics service in production
      // if (import.meta.env.PROD) { analytics.track({ component: name, duration }); }
    };
  }, []);

  const logPerformance = useCallback((_metric: string, _value: number) => {
    if (!import.meta.env.PROD) {
      // console.log(`Performance: ${metric} = ${value}ms`);
    }

    // TODO: Send to analytics service in production
    // if (import.meta.env.PROD) { analytics.track({ metric, value }); }
  }, []);

  useEffect(() => {
    // Measure Core Web Vitals
    const measureWebVitals = () => {
      // First Contentful Paint
      const fcpObserver = new PerformanceObserver((list) => {
        for (const entry of list.getEntries()) {
          if (entry.name === 'first-contentful-paint') {
            logPerformance('FCP', entry.startTime);
          }
        }
      });
      fcpObserver.observe({ entryTypes: ['paint'] });

      // Largest Contentful Paint
      const lcpObserver = new PerformanceObserver((list) => {
        const entries = list.getEntries();
        const lastEntry = entries[entries.length - 1];
        logPerformance('LCP', lastEntry.startTime);
      });
      lcpObserver.observe({ entryTypes: ['largest-contentful-paint'] });

      // First Input Delay
      const fidObserver = new PerformanceObserver((list) => {
        for (const entry of list.getEntries()) {
          const fidEntry = entry as { processingStart?: number; startTime?: number };
          if (fidEntry.processingStart && fidEntry.startTime) {
            logPerformance('FID', fidEntry.processingStart - fidEntry.startTime);
          }
        }
      });
      fidObserver.observe({ entryTypes: ['first-input'] });

      // Cumulative Layout Shift
      const clsObserver = new PerformanceObserver((list) => {
        let clsValue = 0;
        for (const entry of list.getEntries()) {
          const clsEntry = entry as { hadRecentInput?: boolean; value?: number };
          if (clsEntry.hadRecentInput === false && clsEntry.value) {
            clsValue += clsEntry.value;
          }
        }
        logPerformance('CLS', clsValue);
      });
      clsObserver.observe({ entryTypes: ['layout-shift'] });

      // Memory usage (if available)
      if ('memory' in performance) {
      const memory = (
        performance as { memory?: { usedJSHeapSize: number; totalJSHeapSize: number } }
      ).memory;
      if (memory) {
        logPerformance('Memory Used', memory.usedJSHeapSize / 1024 / 1024);
        logPerformance('Memory Total', memory.totalJSHeapSize / 1024 / 1024);
      }
      }
    };

    // Measure page load time
    const measurePageLoad = () => {
      window.addEventListener('load', () => {
        const loadTime = performance.now();
        logPerformance('Page Load', loadTime);
      });
    };

    measureWebVitals();
    measurePageLoad();

    // Cleanup
    return () => {
      // Cleanup observers if needed
    };
  }, [logPerformance]);

  return {
    metrics: null, // Could be enhanced to return actual metrics
    measureComponent,
    logPerformance,
  };
};

// Bundle size analyzer
export const analyzeBundleSize = () => {
  if (!import.meta.env.PROD) {
    // Log bundle size information
  }
};

// Performance budget checker
export const checkPerformanceBudget = (metrics: PerformanceMetrics) => {
  const budget = {
    loadTime: 3000, // 3 seconds
    firstContentfulPaint: 1800, // 1.8 seconds
    largestContentfulPaint: 2500, // 2.5 seconds
    firstInputDelay: 100, // 100ms
    cumulativeLayoutShift: 0.1, // 0.1
  };

  const violations = [];

  if (metrics.loadTime > budget.loadTime) {
    violations.push(`Load time exceeded budget: ${metrics.loadTime}ms > ${budget.loadTime}ms`);
  }

  if (metrics.firstContentfulPaint > budget.firstContentfulPaint) {
    violations.push(
      `FCP exceeded budget: ${metrics.firstContentfulPaint}ms > ${budget.firstContentfulPaint}ms`,
    );
  }

  if (metrics.largestContentfulPaint > budget.largestContentfulPaint) {
    violations.push(
      `LCP exceeded budget: ${metrics.largestContentfulPaint}ms > ${budget.largestContentfulPaint}ms`,
    );
  }

  if (metrics.firstInputDelay > budget.firstInputDelay) {
    violations.push(
      `FID exceeded budget: ${metrics.firstInputDelay}ms > ${budget.firstInputDelay}ms`,
    );
  }

  if (metrics.cumulativeLayoutShift > budget.cumulativeLayoutShift) {
    violations.push(
      `CLS exceeded budget: ${metrics.cumulativeLayoutShift} > ${budget.cumulativeLayoutShift}`,
    );
  }

  if (violations.length > 0) {
    logger.warn('Performance budget violations', { violations });
  }

  return violations;
};
